import { Helmet } from "react-helmet";
import { useState } from "react";
import {
  FaArrowRightArrowLeft,
  FaPlus,
  FaMagnifyingGlass,
  FaFilter,
  FaChevronLeft,
  FaChevronRight,
  FaAnglesLeft,
  FaAnglesRight,
  FaArrowDown,
  FaArrowUp,
  FaTriangleExclamation,
  FaWrench,
  FaUser,
} from "react-icons/fa6";
import { BtnDefault } from "../../../../components/buttons/BtnDefault";
import { Header } from "../../../../components/Header";
import { DashboardLayout } from "../../../../components/layouts/DashboardLayout";
import { useHookLogisticMovement } from "../../../../hooks/useHookLogisticMovement";
import { CreateNewMovementModal } from "./create";

/* ============================================
   HELPERS & SUB-COMPONENTS
   ============================================ */
const formatDate = (dateString) => {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleDateString("id-ID", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const MovementBadge = ({ type }) => {
  const config = {
    stock_in: { label: "Masuk", bg: "bg-emerald-500/12", text: "text-emerald-400", border: "border-emerald-500/20", icon: <FaArrowDown className="text-[10px]" /> },
    stock_out: { label: "Keluar", bg: "bg-blue-500/12", text: "text-blue-400", border: "border-blue-500/20", icon: <FaArrowUp className="text-[10px]" /> },
    damaged: { label: "Rusak", bg: "bg-orange-500/12", text: "text-orange-400", border: "border-orange-500/20", icon: <FaTriangleExclamation className="text-[10px]" /> },
    adjustment: { label: "Penyesuaian", bg: "bg-amber-500/12", text: "text-amber-400", border: "border-amber-500/20", icon: <FaWrench className="text-[10px]" /> },
  };
  const c = config[type] || config.adjustment;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border whitespace-nowrap ${c.bg} ${c.text} ${c.border}`}>
      {c.icon} {c.label}
    </span>
  );
};

const SkeletonRow = ({ cols = 6 }) => (
  <tr className="animate-pulse">{Array.from({ length: cols }).map((_, i) => (<td key={i} className="px-4 py-3"><div className="h-3.5 bg-slate-700/60 rounded-md" style={{ width: `${40 + Math.random() * 50}%` }} /></td>))}</tr>
);

const SelectDark = ({ value, onChange, options, placeholder }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2.5 text-sm rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all appearance-none cursor-pointer" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%2394a3b8' viewBox='0 0 16 16'%3E%3Cpath d='M8 11L3 6h10z'/%3E%3C/svg%3E")`, backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center" }}>
    {placeholder && <option value="">{placeholder}</option>}
    {options.map((opt) => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
  </select>
);

/* ============================================
   MAIN COMPONENT
   ============================================ */
export const LogisticMovement = () => {
  const [showSidebar, setShowSidebar] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const { data, meta, loading, params, setParams, refreshData } = useHookLogisticMovement();

  const handleSearchChange = (val) => setParams((p) => ({ ...p, keyword: val, page: 1 }));
  const handleTypeChange = (val) => setParams((p) => ({ ...p, type: val, page: 1 }));
  const handlePageChange = (page) => setParams((p) => ({ ...p, page }));
  const resetFilter = () => setParams({ keyword: "", type: "", item_id: "", page: 1, per_page: 10 });

  const getPages = () => {
    if (!meta) return [];
    const total = meta.last_page, current = meta.current_page, delta = 2, pages = [];
    const left = Math.max(2, current - delta), right = Math.min(total - 1, current + delta);
    pages.push(1); if (left > 2) pages.push("...");
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < total - 1) pages.push("..."); if (total > 1) pages.push(total);
    return pages;
  };

  const columns = [
    { key: "no", label: "No", align: "center", width: "60px" },
    { key: "date", label: "Tanggal", align: "left" },
    { key: "item", label: "Barang", align: "left" },
    { key: "type", label: "Tipe", align: "center", width: "140px" },
    { key: "qty", label: "Jumlah", align: "center", width: "120px" },
    { key: "notes", label: "Keterangan", align: "left" },
    { key: "user", label: "Oleh", align: "left" },
  ];

  const activeFilterCount = (params.type ? 1 : 0) + (params.keyword ? 1 : 0);

  return (
    <>
      <Helmet>
        <title>Keluar Masuk Barang - Radius Trend</title>
        <meta name="description" content="Catat pergerakan stok barang masuk, keluar, rusak, atau penyesuaian." />
      </Helmet>

      <DashboardLayout showSidebar={showSidebar} setShowSidebar={setShowSidebar}>
        <Header toggleSidebar={() => setShowSidebar(!showSidebar)}>
          <div className="flex items-center gap-3">
            <FaArrowRightArrowLeft className="text-cyan-400 text-lg" />
            <div>
              <h2 className="text-base md:text-xl font-bold text-gray-100">Keluar Masuk Barang</h2>
              <p className="text-xs text-slate-500 hidden sm:block">Riwayat pergerakan stok masuk dan keluar</p>
            </div>
            {meta && <span className="hidden sm:inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">{meta.total} data</span>}
          </div>
          <BtnDefault onClick={() => setIsCreateOpen(true)}>
            <FaPlus className="text-xs hidden sm:block" />
            <span>Catat Barang</span>
          </BtnDefault>
        </Header>

        <main className="p-4 lg:p-6 space-y-5">
          {/* Filter */}
          <section className="rounded-2xl p-4 sm:p-5" style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(51, 65, 85, 0.3)", backdropFilter: "blur(12px)" }}>
            <div className="flex flex-col lg:flex-row lg:items-end gap-4">
              <div className="w-full lg:max-w-xs flex-shrink-0">
                <label className="block text-xs font-semibold text-slate-400 mb-1.5 uppercase tracking-wider">Cari Data</label>
                <div className="relative">
                  <FaMagnifyingGlass className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 text-sm" />
                  <input type="text" placeholder="Nama barang, catatan..." value={params.keyword} onChange={(e) => handleSearchChange(e.target.value)} className="w-full pl-10 pr-4 py-2.5 text-sm rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 placeholder-slate-500 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all" />
                </div>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1.5">
                  <FaFilter className="text-xs text-slate-500" />
                  <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Filter</span>
                  {activeFilterCount > 0 && <span className="min-w-[18px] h-[18px] flex items-center justify-center bg-cyan-600 text-white text-[10px] font-bold rounded-full">{activeFilterCount}</span>}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <SelectDark value={params.type} onChange={handleTypeChange} options={[{ value: "", label: "Semua Tipe" }, { value: "stock_in", label: "Barang Masuk" }, { value: "stock_out", label: "Barang Keluar" }, { value: "damaged", label: "Barang Rusak" }, { value: "adjustment", label: "Penyesuaian" }]} />
                  <div />
                  <button onClick={resetFilter} className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-slate-400 border border-slate-600/40 bg-slate-800/60 hover:text-slate-200 hover:border-slate-500/50 transition-all">Reset</button>
                </div>
              </div>
            </div>
          </section>

          {/* Desktop Table */}
          <section className="hidden lg:block rounded-2xl overflow-hidden" style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(51, 65, 85, 0.3)" }}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b" style={{ background: "rgba(15, 23, 42, 0.7)", borderColor: "rgba(51, 65, 85, 0.3)" }}>
                    {columns.map((col) => (<th key={col.key} className={`px-4 py-3.5 text-xs font-semibold uppercase tracking-wider text-slate-400 ${col.align === "center" ? "text-center" : "text-left"}`} style={col.width ? { width: col.width } : undefined}>{col.label}</th>))}
                  </tr>
                </thead>
                <tbody className="divide-y" style={{ borderColor: "rgba(51, 65, 85, 0.15)" }}>
                  {loading && <>{[...Array(5)].map((_, i) => <SkeletonRow key={i} cols={columns.length} />)}</>}
                  {!loading && data?.length === 0 && (
                    <tr><td colSpan={columns.length} className="px-4 py-16 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: "rgba(51, 65, 85, 0.2)", border: "1px solid rgba(51, 65, 85, 0.2)" }}><FaArrowRightArrowLeft className="text-2xl text-slate-600" /></div>
                        <p className="text-slate-400 font-medium">Belum ada riwayat pergerakan</p>
                      </div>
                    </td></tr>
                  )}
                  {!loading && data?.map((item, index) => {
                    const isOut = item.type === "stock_out" || item.type === "damaged";
                    return (
                      <tr key={item.id} className="transition-colors duration-150 hover:bg-slate-800/30" style={{ background: "transparent" }} onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(0, 180, 216, 0.04)"; }} onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
                        <td className="px-4 py-3 text-center text-slate-500 font-mono text-xs">{meta.from + index}</td>
                        <td className="px-4 py-3 text-slate-300 text-xs whitespace-nowrap">{formatDate(item.created_at)}</td>
                        <td className="px-4 py-3 min-w-[200px]">
                          <p className="font-medium text-gray-100 text-sm truncate">{item.item?.name || "-"}</p>
                          {item.item?.category?.name && <p className="text-[10px] text-slate-500 truncate">{item.item.category.name}</p>}
                        </td>
                        <td className="px-4 py-3 text-center"><MovementBadge type={item.type} /></td>
                        <td className="px-4 py-3 text-center">
                          <span className={`text-sm font-bold ${isOut ? "text-red-400" : "text-emerald-400"}`}>
                            {isOut ? "-" : "+"}{item.quantity} {item.item?.unit || ""}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-400 text-xs max-w-xs truncate">{item.notes || "-"}</td>
                        <td className="px-4 py-3 text-slate-400 text-xs whitespace-nowrap flex items-center gap-2">
                          <FaUser className="text-[10px] flex-shrink-0" />
                          {item.created_by?.name || "-"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {meta && meta.last_page > 1 && (
              <div className="flex items-center justify-between px-5 py-3.5" style={{ borderTop: "1px solid rgba(51, 65, 85, 0.2)" }}>
                <p className="text-xs text-slate-500">Menampilkan <span className="text-slate-300 font-medium">{meta.from}</span> sampai <span className="text-slate-300 font-medium">{meta.to}</span> dari <span className="text-slate-300 font-medium">{meta.total}</span></p>
                <nav className="flex items-center gap-1">
                  <button onClick={() => handlePageChange(1)} disabled={meta.current_page === 1} className="p-2 rounded-lg text-slate-400 hover:text-cyan-400 hover:bg-slate-700/40 disabled:opacity-30 disabled:cursor-not-allowed transition-all"><FaAnglesLeft className="text-xs" /></button>
                  <button onClick={() => handlePageChange(meta.current_page - 1)} disabled={meta.current_page === 1} className="p-2 rounded-lg text-slate-400 hover:text-cyan-400 hover:bg-slate-700/40 disabled:opacity-30 disabled:cursor-not-allowed transition-all"><FaChevronLeft className="text-xs" /></button>
                  {getPages().map((p, i) => p === "..." ? <span key={`dot-${i}`} className="px-2 text-slate-600 text-sm">···</span> : <button key={p} onClick={() => handlePageChange(p)} className={`min-w-[34px] h-9 px-2 rounded-lg text-sm font-medium transition-all duration-200 ${meta.current_page === p ? "bg-cyan-600 text-white shadow-lg shadow-cyan-600/25" : "text-slate-400 hover:text-gray-100 hover:bg-slate-700/40"}`}>{p}</button>)}
                  <button onClick={() => handlePageChange(meta.current_page + 1)} disabled={meta.current_page === meta.last_page} className="p-2 rounded-lg text-slate-400 hover:text-cyan-400 hover:bg-slate-700/40 disabled:opacity-30 disabled:cursor-not-allowed transition-all"><FaChevronRight className="text-xs" /></button>
                  <button onClick={() => handlePageChange(meta.last_page)} disabled={meta.current_page === meta.last_page} className="p-2 rounded-lg text-slate-400 hover:text-cyan-400 hover:bg-slate-700/40 disabled:opacity-30 disabled:cursor-not-allowed transition-all"><FaAnglesRight className="text-xs" /></button>
                </nav>
              </div>
            )}
          </section>

          {/* Mobile Cards */}
          <section className="lg:hidden space-y-3">
            {loading && <>{[...Array(3)].map((_, i) => <div key={i} className="rounded-2xl p-5 animate-pulse" style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(51, 65, 85, 0.3)" }}><div className="h-20 bg-slate-700/40 rounded-lg" /></div>)}</>}
            {!loading && data?.length === 0 && <div className="rounded-2xl p-12 text-center" style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(51, 65, 85, 0.3)" }}><FaArrowRightArrowLeft className="text-3xl text-slate-600 mx-auto mb-3" /><p className="text-slate-400 font-medium text-sm">Belum ada riwayat</p></div>}
            {!loading && data?.map((item) => {
              const isOut = item.type === "stock_out" || item.type === "damaged";
              return (
                <article key={item.id} className="rounded-2xl overflow-hidden" style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(51, 65, 85, 0.3)" }}>
                  <div className="px-4 py-3 flex justify-between items-center border-b" style={{ borderColor: "rgba(51, 65, 85, 0.2)" }}>
                    <span className="text-xs text-slate-400">{formatDate(item.created_at)}</span>
                    <MovementBadge type={item.type} />
                  </div>
                  <div className="p-4 space-y-3">
                    <div>
                      <p className="font-semibold text-gray-100 text-sm">{item.item?.name || "-"}</p>
                      {item.item?.category?.name && <p className="text-xs text-slate-500">{item.item.category.name}</p>}
                    </div>
                    <div className="flex items-center justify-between pt-3 border-t border-slate-700/30">
                      <span className={`text-lg font-bold ${isOut ? "text-red-400" : "text-emerald-400"}`}>
                        {isOut ? "-" : "+"}{item.quantity} <span className="text-xs font-normal text-slate-400">{item.item?.unit || ""}</span>
                      </span>
                      <span className="text-xs text-slate-500 flex items-center gap-1"><FaUser className="text-[10px]" />{item.created_by?.name || "-"}</span>
                    </div>
                    {item.notes && <p className="text-xs text-slate-400 italic bg-slate-800/30 p-2 rounded-lg">"{item.notes}"</p>}
                  </div>
                </article>
              );
            })}
            {meta && meta.last_page > 1 && (
              <div className="flex items-center justify-between pt-3 pb-6 px-1" style={{ borderTop: "1px solid rgba(51, 65, 85, 0.15)" }}>
                <button onClick={() => handlePageChange(meta.current_page - 1)} disabled={meta.current_page === 1} className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-slate-300 disabled:opacity-30" style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(51, 65, 85, 0.3)" }}><FaChevronLeft className="text-xs" />Sebelumnya</button>
                <span className="text-sm font-medium text-slate-400"><span className="text-cyan-400">{meta.current_page}</span><span className="text-slate-600 mx-1">/</span>{meta.last_page}</span>
                <button onClick={() => handlePageChange(meta.current_page + 1)} disabled={meta.current_page === meta.last_page} className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-slate-300 disabled:opacity-30" style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(51, 65, 85, 0.3)" }}>Berikutnya<FaChevronRight className="text-xs" /></button>
              </div>
            )}
          </section>
        </main>
      </DashboardLayout>

      <CreateNewMovementModal refresh={refreshData} openModal={isCreateOpen} setOpenModal={setIsCreateOpen} />
    </>
  );
};